import { useCallback, useState, forwardRef, useImperativeHandle } from "react";
import { Link } from "react-router-dom";
import messages from "messages";
// components
import Checkbox from "components/checkbox/Checkbox";
import PaneLayout from "../pane-layout";
import RequiredAsterisk from "components/required-asterisk";
import StripeSetupButton from "components/Dashboard/DoctorDashboard/components/stripe-setup-button";
// hooks
import { useUser } from "contexts/UserContext";
// types
import type { PaneProps, PaneRefProps } from "../../MainSetup";
// services
import postOrchidProCompleteSchedulerTutorial from "services/postOrchidProCompleteSchedulerTutorial";
// styles
import styles from "./styles.module.scss";
// constants
import { PRO_ROUTES } from "constants/proRoutes";
import { SHOW_TUTORIAL_COMPLETE_MODAL } from "constants/showTutorialCompleteModal";
import { AmplitudePageName, track } from "utils/amplitude";

/**
 * orchid contact email
 */
const ORCHID_EMAIL = "orchid.exchange.customer@gmail.com";
const AMPLITUDE_COMPONENT_NAME = "payment-method-pane";

const ForwardedPaymentMethodPane = forwardRef<
  PaneRefProps,
  PaneProps & { amplitudePageName: AmplitudePageName }
>(function PaymentMethodPane({ onShowModal, amplitudePageName, ...rest }, ref) {
  const { user } = useUser();
  const [checked, setChecked] = useState(false);

  const handleApply = useCallback(async () => {
    // call the complete scheduler tutorial
    await postOrchidProCompleteSchedulerTutorial();
    // should set payment method value
    window.localStorage.setItem(
      SHOW_TUTORIAL_COMPLETE_MODAL.key,
      SHOW_TUTORIAL_COMPLETE_MODAL.value,
    );
    // go to the pro calendar page
    window.location.assign(PRO_ROUTES.calendar);
    return;
  }, []);

  useImperativeHandle(ref, () => ({
    handleApply,
  }));

  return (
    <PaneLayout
      Title={messages.setupPaymentMethod}
      Subtitle={messages.inOrderToActivate}
      {...rest}
    >
      {!user?.has_stripe_account_connected ? (
        <>
          <Checkbox
            className={styles["payment-method-pane__body"]}
            variant={"rectangle"}
            checked={checked}
            onChange={(e) => {
              track(
                amplitudePageName,
                [
                  AMPLITUDE_COMPONENT_NAME,
                  "independent-contractor-agreement-checkbox",
                ],
                "click",
              );
              setChecked(e.target.checked);
            }}
            label={
              <label className={styles["payment-method-pane__checkbox-label"]}>
                {messages.iHaveRead}
                <Link
                  className={styles["payment-method-pane__link"]}
                  to="/independent-contractor-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track(
                      amplitudePageName,
                      [
                        AMPLITUDE_COMPONENT_NAME,
                        "independent-contractor-agreement-link",
                      ],
                      "click",
                    )
                  }
                >
                  {messages.independentContractorAgreement}
                </Link>
                <RequiredAsterisk />
              </label>
            }
          />
          <StripeSetupButton
            disabled={!checked || user?.has_stripe_account_connected}
            variant="outline-primary"
            children={messages.setUpPayment}
            className={styles["payment-method-pane__connect-btn"]}
            amplitudePageName={amplitudePageName}
            amplitudeComponentName={AMPLITUDE_COMPONENT_NAME}
          />
        </>
      ) : (
        <>
          <p className={styles["payment-method-pane__connected-label"]}>
            {messages.yourStripeAccountIsConnected}
          </p>
          <p className={styles["payment-method-pane__connected-text"]}>
            {messages.ifThisIsAnError}
            <a
              className={styles["payment-method-pane__connected-email"]}
              href={`mailto:${ORCHID_EMAIL}`}
            >
              {ORCHID_EMAIL}
            </a>
          </p>
        </>
      )}
    </PaneLayout>
  );
});
export default ForwardedPaymentMethodPane;
